import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Loader2, Sparkles, PlayCircle, Gauge, Smile, Meh, Frown } from "lucide-react";

const BAND_STYLES = {
  promoter: { cls: "bg-[#eaf8de] text-[#3f7c21]", icon: Smile },
  passive: { cls: "bg-[#fff7e6] text-[#a87700]", icon: Meh },
  detractor: { cls: "bg-[#fff0f5] text-[#c01974]", icon: Frown },
};

export default function Nps() {
  const [summary, setSummary] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [batch, setBatch] = useState({ count: 10, force_promoter: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, lst] = await Promise.all([
        api.get("/nps/summary"),
        api.get(`/nps/submissions${filter ? `?band=${filter}` : ""}`),
      ]);
      setSummary(s.data);
      setSubmissions(lst.data);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(""), 4000); };

  const simulate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/nps/simulate-batch", batch);
      flash(`Generated ${data.generated} submissions · ${data.promoter_invites_sent} promoter invite(s) fired`);
      await load();
    } catch (e) {
      flash(formatApiError(e.response?.data?.detail) || "Batch failed");
    } finally { setBusy(false); }
  };

  const runSweep = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/nps/run-promoter-sweep");
      flash(`Sweep processed ${data.processed} · sent ${data.invited} invite(s)`);
      await load();
    } catch (e) {
      flash(formatApiError(e.response?.data?.detail) || "Sweep failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl flex items-center gap-2">
            <Gauge className="h-6 w-6 text-[#66cc33]" /> NPS Automation
          </h1>
          <p className="text-sm text-[#5c665a]">
            Post-delivery NPS widget submissions. Promoters (score ≥ 9) automatically get a warm Refer &amp; Earn invite on WhatsApp (subject to tenure + cooldown).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button className="admin-btn-ghost" onClick={runSweep} disabled={busy} data-testid="nps-sweep-btn">
            <PlayCircle className="h-4 w-4" /> Run promoter sweep now
          </button>
        </div>
      </div>

      {toast && <div className="rounded-lg bg-[#eaf8de] px-4 py-2 text-sm font-semibold text-[#3f7c21]" data-testid="nps-toast">{toast}</div>}

      {summary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6" data-testid="nps-summary">
          <StatCard label="NPS score (30d)" value={summary.nps_score} suffix="" color="text-[#1a1c19]" big />
          <StatCard label="Promoters" value={summary.promoters} color="text-[#66cc33]" />
          <StatCard label="Passives" value={summary.passives} color="text-[#a87700]" />
          <StatCard label="Detractors" value={summary.detractors} color="text-[#c01974]" />
          <StatCard label="Invites triggered" value={summary.invites_triggered} color="text-[#ff3399]" />
          <StatCard label="Pending sweep" value={summary.pending_sweep} color="text-[#5c665a]" />
        </div>
      )}

      <div className="admin-card">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#5c665a] flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#ff3399]" /> Simulate batch
        </h3>
        <p className="mt-1 text-xs text-[#5c665a]">
          Generates synthetic post-delivery NPS submissions sampled from seeded referrers (55% promoter / 30% passive / 15% detractor, unless forced).
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-[120px_auto_auto]">
          <label className="block">
            <span className="text-xs font-semibold text-[#5c665a]">How many</span>
            <input
              type="number" min="1" max="100"
              value={batch.count}
              onChange={(e) => setBatch((b) => ({ ...b, count: Number(e.target.value) }))}
              className="admin-input mt-1 w-full"
              data-testid="nps-batch-count"
            />
          </label>
          <label className="flex items-center gap-2 self-end pb-2 text-xs font-semibold text-[#5c665a]">
            <input
              type="checkbox"
              checked={batch.force_promoter}
              onChange={(e) => setBatch((b) => ({ ...b, force_promoter: e.target.checked }))}
              data-testid="nps-batch-force-promoter"
            />
            Force all promoters (for demo)
          </label>
          <button className="admin-btn self-end" onClick={simulate} disabled={busy} data-testid="nps-batch-run">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate
          </button>
        </div>
      </div>

      <div className="admin-card overflow-x-auto p-0">
        <div className="flex items-center justify-between border-b border-[#e2e8e2] px-4 py-3">
          <h3 className="font-semibold">Latest submissions</h3>
          <select
            className="admin-input max-w-[160px]"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="nps-band-filter"
          >
            <option value="">All bands</option>
            <option value="promoter">Promoters</option>
            <option value="passive">Passives</option>
            <option value="detractor">Detractors</option>
          </select>
        </div>
        <table className="min-w-full text-sm" data-testid="nps-table">
          <thead className="bg-[#fafcfa] text-left text-xs uppercase tracking-wider text-[#5c665a]">
            <tr>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Score</th>
              <th className="px-4 py-3">Band</th>
              <th className="px-4 py-3">Comment</th>
              <th className="px-4 py-3">Invite</th>
              <th className="px-4 py-3">Submitted</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e2e8e2]">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center"><Loader2 className="inline h-4 w-4 animate-spin" /></td></tr>
            ) : submissions.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-[#5c665a]">No submissions yet. Use "Simulate batch" above.</td></tr>
            ) : submissions.map((s) => {
              const bandMeta = BAND_STYLES[s.band] || BAND_STYLES.passive;
              const Icon = bandMeta.icon;
              return (
                <tr key={s.id}>
                  <td className="px-4 py-2 font-mono text-xs">{s.phone}</td>
                  <td className="px-4 py-2">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#fafcfa] font-extrabold text-[#1a1c19]">{s.score}</span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${bandMeta.cls}`}>
                      <Icon className="h-3 w-3" /> {s.band}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-[#5c665a]">{s.comment || "—"}</td>
                  <td className="px-4 py-2 text-[11px]">
                    {s.invite_triggered ? (
                      <span className="font-bold text-[#3f7c21]">sent</span>
                    ) : s.score >= 9 ? (
                      <span className="text-[#5c665a]">skipped: {s.invite_reason || "pending"}</span>
                    ) : (
                      <span className="text-[#5c665a]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-[#5c665a]">{new Date(s.submitted_at).toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, suffix = "", big = false }) {
  return (
    <div className="admin-card">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5c665a]">{label}</div>
      <div className={`mt-1 ${big ? "text-3xl" : "text-2xl"} font-extrabold ${color}`}>{value}{suffix}</div>
    </div>
  );
}
